import './global-shim';
import React from 'react';
import {createRoot} from 'react-dom/client';
import {ErrorBoundary} from 'react-error-boundary';

import App from './App';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';
import staticAssetPreloader from './services/StaticAssetPreloader';
import cachePerformanceMonitor from './services/CachePerformanceMonitor';
import serviceWorkerUpdateManager from './services/ServiceWorkerUpdateManager';
import staticAssetLoadingManager from './stores/StaticAssetLoadingManager';
import {initChunkLoadErrorHandler} from './utils/errors/chunkLoadErrorHandler';
// import reportWebVitals from './reportWebVitals';

import {initNetworkListener} from './hub/network';
import {initAuthListener} from './hub/auth';
import {registerConflictHandlers} from './graphql/conflictResolution';
import {startPeriodicMetricsLogging} from './graphql/conflictResolution/logger';

import {UnexpectedErrorMessage} from './ui/elements/UnexpectedErrorMessage';

import {logErrorToSentry} from './utils/errors/logErrorToSentry';
import {initStateAuto} from '@stores/state';
import {AppState} from '@stores/AppState';
import {AppStateProvider} from '@stores/AppStateContext';
import {registerAppState} from '@stores/appStateRegistry';

import {initializePlatform} from './platform';
import {unsavedChangesManager} from '@stores/UnsavedChangesManager';
import {initLocale} from './navigation/initLocale';
import {
    FEATURE_FLAGS,
    getLocalFeatureFlagOverrides,
    setLocalFeatureFlag,
} from '@stores/featureFlags';


// Initialize chunk load error handler first (before any dynamic imports)
initChunkLoadErrorHandler();

initAuthListener();

// Register conflict handlers for automatic conflict resolution
registerConflictHandlers();

// Start periodic metrics logging for conflict resolution
startPeriodicMetricsLogging();

async function initializeApp() {
    const serviceRegistry = await initializePlatform();

    const appState = new AppState(serviceRegistry);
    registerAppState(appState);

    await initLocale(appState.rootStore);
    appState.routerService.initRouting();
    initNetworkListener(appState.rootStore);

    await initStateAuto(appState);

    return appState;
}

async function startApplication() {
    try {
        const appState = await initializeApp();

        if (import.meta.env.VITE_APP_ENV === 'development') {
            // @ts-ignore
            window.appState = appState;
        }

        const container = document.getElementById('root')!;
        const root = createRoot(container);

        root.render(
            <React.StrictMode>
                <ErrorBoundary fallback={<UnexpectedErrorMessage/>}>
                    <AppStateProvider appState={appState}>
                        <App/>
                    </AppStateProvider>
                </ErrorBoundary>
            </React.StrictMode>,
        );
    } catch (error: unknown) {
        console.log('!!error!!', error);
        try {
            if (
                error === 'No current user'
                || (error as Error).message === 'No current user'
                || (error as Error).name === 'NotAuthorizedException'
                || (error as Error).name === 'UserUnAuthenticatedException'
                || (error as Error).name === 'UserNotFoundException'
            ) {
                window.location.href = import.meta.env.VITE_BASE_PATH + '/sign-in';
            } else {
                logErrorToSentry(error as string);
            }
        } catch (clearError: unknown) {
            logErrorToSentry(clearError as string);
        }
    }
}

if (import.meta.env.VITE_APP_ENV === 'development') {
    // @ts-ignore
    window.unsavedChangesManager = unsavedChangesManager;
    // @ts-ignore
    window.generateAsciiArt = (doc: any) => {
        let output = '';

        function traverse(node: any, pos = 0, depth = 0, indent = '') {
            output += `${indent}${node.type.name} (${pos}, ${depth})\n`;
            const nextIndent = `${indent}  `;
            node.forEach((child: any, offset: any) => {
                traverse(child, pos + offset + 1, depth + 1, nextIndent);
            });
        }

        traverse(doc);
        return output;
    };
    // @ts-ignore
    window.enableFeatureFlag = (flag: string) => setLocalFeatureFlag(flag, true);
    // @ts-ignore
    window.listFeatureFlags = () => ({
        featureFlags: getLocalFeatureFlagOverrides(),
        constants: FEATURE_FLAGS,
    });
}

startApplication();

// Enable service worker for caching
serviceWorkerRegistration.register({
    onSuccess: (registration) => {
        // console.log('[SW] Service worker registered successfully');

        // Start preloading critical assets after service worker is ready
        setTimeout(() => {
            staticAssetPreloader.preloadCriticalAssets();

            // Log initial cache metrics
            // console.log('[CACHE] Initial metrics:', cachePerformanceMonitor.getMetrics());

            // Mark critical assets as loaded after preloading
            setTimeout(() => {
                staticAssetLoadingManager.markCriticalAssetsComplete();
            }, 2000);
        }, 1000);
    },
    onUpdate: (registration) => {
        // console.log('[SW] New service worker available');
        // The ServiceWorkerUpdateManager will handle the update logic
        serviceWorkerUpdateManager.checkForUpdates();
    }
});
